import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import CloudStorage from "../../../../../CloudStorage/CloudStorage";
import { urtoast } from "../../../../Layouts/CustomPopup/eventEmitter";
import DisplayFileProper from "../../../../Common/FilesRelevent/DisplayFileProper";
import { useFileValidator } from "../../../../../hooks/useFileValidator";
import UploadLoader from "../../../../Common/Loader/UploadLoader";

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip",
];

export default function AllFilesUpload({ worksampleFiles, setWork_sample }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false); // 🛡️ Added state for batch upload loader
  const { validating, validateFiles } = useFileValidator();

  const handleUpload = async (files) => {
    // Validate all files via backend API
    const isValid = await validateFiles(files);
    if (!isValid) return;

    setIsUploading(true); // 🟢 Start loader before batch upload begins

    const uploadPromises = files.map((file) => {
      if (file.size > 10 * 1024 * 1024) {
        urtoast(`File size exceeds 10MB limit.`);
        return Promise.reject(new Error("File size exceeds limit."));
      }

      const uniqueId = uuidv4(); // Generate a unique ID for each file
      const storageRef = ref(
        getStorage(),
        `/Category/${uniqueId}-${file.name}`
      );
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return uploadTask
        .then(async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          return { name: file.name, file: url }; // Include the unique ID with the file URL
        })
        .catch((error) => {
          urtoast(`Error uploading ${file.name}: ${error.message}`);
          throw error;
        });
    });

    Promise.all(uploadPromises)
      .then((urls) => {
        const updatedFiles = [
          ...(Array.isArray(selectedFiles) ? selectedFiles : []),
          ...(Array.isArray(urls) ? urls : []),
        ];
        setSelectedFiles(updatedFiles);
        setWork_sample((prevData) => [
          ...(Array.isArray(prevData) ? prevData : []),
          ...(Array.isArray(urls) ? urls : []),
        ]);
        setIsUploading(false); // 🔴 Stop loader on success
      })
      .catch((error) => {
        console.error("Error uploading files: ", error);
        setIsUploading(false); // 🔴 Stop loader on error
      });
  };

  const onDrop = (acceptedFiles) => {
    handleUpload(acceptedFiles);
  };

  // 🛡️ Disable dropzone while validating or uploading
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: validating || isUploading, 
  });

  const handleDeleteImage = (item) => {
    if (isUploading) return; // Prevent deletion during upload
    
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((file) => file.file !== item.file)
    );
    setWork_sample((prevFiles) =>
      prevFiles.filter((file) => file.file !== item.file)
    );
  };

  useEffect(() => {
    setSelectedFiles(worksampleFiles);
  }, [worksampleFiles]);

  return (
    <div className="add-portfolio position-relative">
      {/* Show loader during validation OR uploading */}
      {(validating || isUploading) && <UploadLoader />}
      
      <div className="row">
        {selectedFiles && selectedFiles.length === 0 ? (
          <div className="col-md-4">
            <div className="image-container">
              <img
                src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/file-upload.png`}
                className="cover-img w-100"
                alt="Certificate"
              />
            </div>
          </div>
        ) : (
          selectedFiles &&
          selectedFiles.map((item, index) => (
            <div className="image-container col-md-4 " key={index}>
              <DisplayFileProper item={item} />
              <button
                type="button"
                className="delete-button"
                onClick={() => handleDeleteImage(item)}
                disabled={isUploading} // Disable delete while uploading
              >
                <img
                  src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/delete-icon.svg`}
                  alt="Delete"
                />
              </button>
            </div>
          ))
        )}
      </div>
      <div
        {...getRootProps({ className: "dropzone borderstylex drag-drop" })}
        style={{
          cursor: (validating || isUploading) ? "not-allowed" : "pointer",
          opacity: (validating || isUploading) ? 0.7 : 1,
        }}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the files here...</p>
        ) : (
          <p>
            {isUploading ? "Uploading files..." : validating ? "Validating files..." : (
              <>Drag and drop or <span>Browse file</span></>
            )}
          </p>
        )}
      </div>
    </div>
  );
}